import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Building2, Users, TrendingUp, MapPin, Search, FileText, Calculator, Handshake, ArrowRight } from "lucide-react";

const Services = () => {
  const mainServices = [
    {
      icon: Building2,
      title: "Office Leasing",
      description: "Comprehensive office leasing services for landlords and tenants",
      features: [
        "Market analysis and pricing strategy",
        "Property marketing and promotion",
        "Tenant screening and qualification",
        "Lease negotiation and documentation",
        "Space planning and design consultation"
      ],
      image: "https://images.unsplash.com/photo-1760124056893-6b2e8a11745a?w=600&auto=format&fit=crop&q=80"
    },
    {
      icon: TrendingUp,
      title: "Investment Sales",
      description: "Strategic investment opportunities with comprehensive market analysis",
      features: [
        "Property valuation and appraisal",
        "Investment analysis and modeling",
        "Market research and due diligence",
        "Buyer and seller representation",
        "1031 exchange facilitation"
      ],
      image: "https://images.unsplash.com/photo-1767176736287-eb90b3c1fc61?w=600&auto=format&fit=crop&q=80"
    },
    {
      icon: Users,
      title: "Tenant Representation",
      description: "Expert representation to secure the perfect space for your business",
      features: [
        "Space needs analysis and planning",
        "Market survey and site selection",
        "Lease negotiation and terms review",
        "Relocation project management",
        "Ongoing portfolio management"
      ],
      image: "https://images.unsplash.com/photo-1642522029691-029b5a432954?w=600&auto=format&fit=crop&q=80"
    },
    {
      icon: MapPin,
      title: "Market Analysis",
      description: "In-depth market research and analysis for informed decisions",
      features: [
        "Comparative market analysis",
        "Demographic and economic studies",
        "Absorption and vacancy tracking",
        "Rent and sale price trends",
        "Development feasibility studies"
      ],
      image: "https://images.unsplash.com/photo-1761002278107-dc5d4f800834?w=600&auto=format&fit=crop&q=80"
    }
  ];

  const additionalServices = [
    {
      icon: Search,
      title: "Site Selection",
      description: "Strategic location analysis for optimal business positioning"
    },
    {
      icon: FileText,
      title: "Lease Administration",
      description: "Comprehensive lease management and administration services"
    },
    {
      icon: Calculator,
      title: "Financial Analysis",
      description: "Detailed financial modeling and investment analysis"
    },
    {
      icon: Handshake,
      title: "Consulting Services",
      description: "Expert advisory services for complex real estate decisions"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <img 
                src="./images/KWCommercial_Stacked_RGB.png" 
                alt="KW Commercial" 
                className="h-12 w-auto"
              />
            </div>
            <div className="hidden md:flex space-x-8">
              <a href="/" className="text-foreground hover:text-primary transition-smooth">Home</a>
              <a href="/services" className="text-primary font-semibold">Services</a>
              <a href="/properties" className="text-foreground hover:text-primary transition-smooth">Properties</a>
              <a href="/about" className="text-foreground hover:text-primary transition-smooth">About</a>
              <a href="/contact" className="text-foreground hover:text-primary transition-smooth">Contact</a>
            </div>
            <Button className="btn-primary">
              Get Started
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-20 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-6">Our Services</h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Comprehensive commercial real estate services designed to maximize value 
            and minimize complexity for our clients.
          </p>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {mainServices.map((service, index) => {
              const Icon = service.icon;
              const isEven = index % 2 === 0;
              
              return (
                <div key={index} className={`grid lg:grid-cols-2 gap-12 items-center ${!isEven ? 'lg:grid-flow-col-dense' : ''}`}>
                  <div className={isEven ? 'lg:order-1' : 'lg:order-2'}>
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mr-4">
                        <Icon className="h-6 w-6 text-primary" />
                      </div>
                      <h2 className="text-3xl font-bold">{service.title}</h2>
                    </div>
                    <p className="text-lg text-muted-foreground mb-6">
                      {service.description}
                    </p>
                    <ul className="space-y-3 mb-8">
                      {service.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-center">
                          <div className="w-2 h-2 bg-primary rounded-full mr-3"></div>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button className="btn-primary">
                      Learn More <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                  <div className={isEven ? 'lg:order-2' : 'lg:order-1'}>
                    <img 
                      src={service.image} 
                      alt={service.title}
                      className="rounded-lg shadow-elegant w-full h-[400px] object-cover"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-20 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Additional Services</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Specialized services to support your commercial real estate objectives
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {additionalServices.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card key={index} className="shadow-card hover:shadow-hover transition-smooth text-center">
                  <CardHeader>
                    <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                      <Icon className="h-8 w-8 text-primary" />
                    </div>
                    <CardTitle className="text-xl">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription>
                      {service.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Get Started?</h2>
          <p className="text-xl text-muted-foreground mb-8">
            Contact us today to discuss how we can help you achieve your commercial real estate goals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="btn-primary px-8">
              Schedule Consultation
            </Button>
            <Button size="lg" variant="outline" className="px-8">
              View Properties
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-foreground text-background py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <img 
                src="./images/KWCommercial_Stacked_RGB.png" 
                alt="KW Commercial" 
                className="h-12 w-auto mb-4 brightness-0 invert"
              />
              <p className="text-background/80">
                Your trusted partner in commercial real estate.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-background/80">
                <li>Office Leasing</li>
                <li>Investment Sales</li>
                <li>Tenant Representation</li>
                <li>Market Analysis</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Properties</h4>
              <ul className="space-y-2 text-background/80">
                <li>Office Buildings</li>
                <li>Retail Centers</li>
                <li>Industrial Warehouses</li>
                <li>Mixed-Use Developments</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-background/80">
                <li>(555) 123-4567</li>
                <li>info@kwcommercial.com</li>
                <li>123 Business Plaza, Suite 100</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-background/20 mt-8 pt-8 text-center text-background/60">
            <p>&copy; 2024 KW Commercial. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Services;